/**
 * SubHeaderWithTabs — structural primitive for a 2-row page sub-header
 * (header row + scrollable tab strip).
 *
 * Scope: structural pattern only. Owns the row stacking, the tab strip's
 * horizontal-scroll behaviour, the left/right scroll affordances that
 * appear only on overflow, click handling, and active-tab
 * `scrollIntoView` on selection. Does NOT own visual chrome (colours,
 * paddings, borders, hover states, badge geometry) — every consumer
 * brings its own styling via the className-override props so the
 * primitive can sit on both Athena's grey ProjectHeader surface and the
 * Vulnerability Management Report page's white scan-card surface
 * without forcing one look on the other.
 *
 * Used by:
 *   - features/projects/components/ProjectHeader/ProjectHeader.jsx
 *   - features/vulnerability_management/Report.jsx (Phase 2)
 *
 * Lifted from the inline implementation at
 * features/projects/components/ProjectHeader/ProjectHeader.jsx#L196-229
 * (scroll-affordance math) + #L327-367 (tab-strip JSX). Same behaviour;
 * extracted so both pages can share it.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../../ui/Icon';
import { Tabs, TabsList, TabsTrigger } from '../../ui/Tabs2';
import styles from './SubHeaderWithTabs.module.css';

const cn = (...names) => names.filter(Boolean).join(' ');

/**
 * @param {object} props
 * @param {React.ReactNode}  [props.header]         Row 1 content; consumer-rendered.
 * @param {Array<{ id: string, label: React.ReactNode, title?: string }>} props.tabs  Tab config.
 * @param {string}           props.activeTabId      Currently selected tab id.
 * @param {(id: string) => void} props.onTabChange  Fired on tab click.
 * @param {boolean}          [props.showScrollAffordances=true]
 *                                                   Render the left/right scroll buttons
 *                                                   when the tab strip overflows.
 * @param {string}           [props.className]      Outer root class.
 * @param {string}           [props.tabsWrapClassName] The row 2 outer wrap (where the
 *                                                   surface chrome usually lives).
 * @param {string}           [props.tabsStripClassName] The horizontal-scroll container.
 * @param {string}           [props.tabClassName]   Each tab button.
 * @param {string}           [props.activeTabClassName] Active variant; applied on top of
 *                                                   `tabClassName` for the selected tab.
 * @param {'segmented'|'line'} [props.tabVariant]   Opt-in 2.0 tab styling applied by the
 *                                                   primitive itself: `segmented` (active =
 *                                                   --background pill on a --muted track) or
 *                                                   `line` (active = --primary underline).
 *                                                   Pair `segmented` with a --muted
 *                                                   `tabsWrapClassName` for the track surface.
 *                                                   Omit to keep the pure className-driven
 *                                                   behaviour. Consumer tab/activeTab classes
 *                                                   still apply on top.
 * @param {string}           [props.scrollBtnClassName] Each scroll-affordance button.
 * @param {React.ReactNode}  [props.leadSlot]       Control pinned to the far-LEFT of
 *                                                   the tab row (before the tabs).
 * @param {React.ReactNode}  [props.trailSlot]      Control pinned to the far-RIGHT of
 *                                                   the tab row (after the tabs).
 */
const SubHeaderWithTabs = ({
  header,
  tabs = [],
  activeTabId,
  onTabChange,
  showScrollAffordances = true,
  className,
  tabsWrapClassName,
  tabsStripClassName,
  tabClassName,
  activeTabClassName,
  tabVariant,
  scrollBtnClassName,
  leadSlot,
  trailSlot,
}) => {
  // When `tabVariant` is set, the strip renders through the 2.0 Tabs (Tabs2)
  // components so the segmented/line styling comes straight from Tabs2 (single
  // source — no copied CSS). Otherwise it falls back to the className-driven
  // plain-button rendering (e.g. the vuln Report sub-header).
  const useTabs2 = tabVariant === 'segmented' || tabVariant === 'line';
  const listVariant = tabVariant === 'line' ? 'line' : 'default';
  const tabScrollRef = useRef(null);
  const activeTabRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Scroll position → which scroll-affordance buttons should be visible.
  // `4` pixel slop so a near-edge scroll doesn't flicker the button on
  // and off.
  const updateScrollButtons = useCallback(() => {
    const el = tabScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    if (!showScrollAffordances) return undefined;
    const el = tabScrollRef.current;
    if (!el) return undefined;
    updateScrollButtons();
    el.addEventListener('scroll', updateScrollButtons, { passive: true });
    // ResizeObserver catches: viewport resize, layout shifts, tab-set
    // changes. Without it, `canScroll*` goes stale when the strip's
    // width changes from anything other than a scroll event.
    const ro = new ResizeObserver(updateScrollButtons);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollButtons);
      ro.disconnect();
    };
  }, [tabs.length, showScrollAffordances, updateScrollButtons]);

  // When the active tab changes (e.g., URL `?tab=` updates from outside
  // the strip), scroll it into view so a programmatic selection never
  // strands the active tab off-screen.
  useEffect(() => {
    // Find the active tab to scroll it into view. In Tabs2 mode the active
    // trigger isn't ours to ref, so we locate it inside our scroll container by
    // its `data-state="active"` sticker; otherwise we use the ref on our button.
    const container = tabScrollRef.current;
    if (!container) return;
    const target = useTabs2
      ? container.querySelector('[data-state="active"]')
      : activeTabRef.current;
    // `scrollIntoView` is not implemented in JSDOM; guard for tests / older browsers.
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, [activeTabId, useTabs2]);

  const scrollTabs = (direction) => {
    tabScrollRef.current?.scrollBy({ left: direction * 200, behavior: 'smooth' });
  };

  return (
    <div className={cn(styles.root, className)}>
      {/* `header` is rendered directly as the first child of `.root`
          — no wrapping `<div>`. This keeps the consumer's JSX (which
          paints the surface chrome itself, e.g. `.scanHeader` /
          `.bar`) as the immediate background owner with no
          intermediate node that could leak a stray rule. */}
      {header}

      {tabs.length > 0 && (
        <div className={cn(styles.tabsWrap, tabsWrapClassName)}>
          {leadSlot && <div className={styles.slot}>{leadSlot}</div>}

          {showScrollAffordances && canScrollLeft && (
            <button
              type="button"
              className={cn(styles.scrollBtn, scrollBtnClassName)}
              onClick={() => scrollTabs(-1)}
              aria-label="Scroll tabs left"
            >
              <Icon name="chevron-left" size={16} />
            </button>
          )}

          <div
            className={cn(styles.tabsStrip, tabsStripClassName)}
            ref={tabScrollRef}
            role={useTabs2 ? undefined : 'tablist'}
          >
            {useTabs2 ? (
              /* 2.0 Tabs (Tabs2) — styling/active-state/keyboard come from Tabs2
                 via the `variant` prop (no copied CSS). `tabsContents` makes the
                 Tabs root box-less so TabsList is the direct scroll child. No
                 TabsContent — the "content" is the routed page below. */
              <Tabs
                value={activeTabId ?? ''}
                onValueChange={(v) => onTabChange?.(v)}
                className={styles.tabsContents}
              >
                <TabsList variant={listVariant}>
                  {tabs.map((tab) => (
                    <TabsTrigger key={tab.id} value={tab.id} disabled={tab.disabled} title={tab.title}>
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            ) : (
              tabs.map((tab) => {
                const isActive = tab.id === activeTabId;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    ref={isActive ? activeTabRef : null}
                    className={cn(tabClassName, isActive && activeTabClassName)}
                    onClick={() => onTabChange?.(tab.id)}
                    title={tab.title}
                    role="tab"
                    aria-selected={isActive}
                  >
                    {tab.label}
                  </button>
                );
              })
            )}
          </div>

          {showScrollAffordances && canScrollRight && (
            <button
              type="button"
              className={cn(styles.scrollBtn, scrollBtnClassName)}
              onClick={() => scrollTabs(1)}
              aria-label="Scroll tabs right"
            >
              <Icon name="chevron-right" size={16} />
            </button>
          )}

          {trailSlot && <div className={styles.slot}>{trailSlot}</div>}
        </div>
      )}
    </div>
  );
};

export default SubHeaderWithTabs;
